import React, { useEffect, useContext } from 'react'
import { Adsense } from '@ctrl/react-adsense'
import { ReactAdSense } from './react-adsense'
import { GoogleAdContext } from './google-ad-context'
import { getCustomAdUnit } from './google-ad-variant'
import GoogleAdCta from '../google-ad-cta'
import './google_ad.css'

const GoogleAd = ({ adKey, adSource, adPosition, fromContent = false, isSidePanelAd = false }) => {
  const { variantsEnabled, previousRoute, setPreviousRoute, adsRequestSent, setAdsRequestSent } = useContext(GoogleAdContext)
  const isDev = ['mt3.dev', 'ewnova.dev', 'localhost'].some(host => window.location.hostname.endsWith(host))
  const adClass = `mt-${adKey}-${adPosition}`
  const currentRoute = window.location.pathname.split('/')[1]
  const key = `${adKey}#${adPosition}#${currentRoute}`

  // Fallback of PENG-2242 when variants are disabled
  if (!variantsEnabled && !isDev) {
    if (previousRoute && previousRoute !== currentRoute) {
      setPreviousRoute(null)
      if (adsRequestSent) {
        (window.adsbygoogle = window.adsbygoogle || []).pauseAdRequests = 1
        setAdsRequestSent(false)
      }
    } else {
      setPreviousRoute(currentRoute)
    }

    return (
      <div key={key}>
        <Adsense
          className={adClass}
          client={process.env.REACT_APP_GOOGLE_ADSENSE_CLIENT}
          slot={process.env.REACT_APP_GOOGLE_ADSENSE_SLOT}
          style={{ display: 'block' }}
          responsive='true'
          layout={fromContent ? 'in-article' : ''}
          format='fluid'
        />
        <GoogleAdCta isSidePanelAd={isSidePanelAd} />
      </div>
    )
  }

  // Always render GoogleAdCxt in dev or when variants are enabled
  return (
    <GoogleAdCxt
      isDev={isDev}
      adClass={adClass}
      adKey={adKey}
      adSource={adSource}
      adPosition={adPosition}
      fromContent={fromContent}
      isSidePanelAd={isSidePanelAd}
    />
  )
}

const GoogleAdCxt = ({ adKey, adSource, adPosition, fromContent = false, isSidePanelAd = false, adClass, isDev }) => {
  // const isDev = ['mt3.dev', 'ewnova.dev', 'localhost'].some(host => window.location.hostname.endsWith(host))
  const currentRoute = window.location.pathname.split('/')[1]
  // const adClass = `mt-${adKey}-${adPosition}`
  const key = `${adKey}#${adPosition}#${currentRoute}`

  const { pushedAds, previousRoute, setPreviousRoute, adsRequestSent, setAdsRequestSent, selectedVariant } = useContext(GoogleAdContext)

  const selectedAdUnit = selectedVariant.type === 'custom'
    ? getCustomAdUnit(adPosition)
    : selectedVariant.type

  useEffect(() => {
    if (previousRoute && previousRoute !== currentRoute && pushedAds.current.size > 0) {
      pushedAds.current.clear()
      setPreviousRoute(null)
      if (adsRequestSent) {
        (window.adsbygoogle = window.adsbygoogle || []).pauseAdRequests = 1
        setAdsRequestSent(false)
      }
    }

    if (pushedAds.current.has(key)) return

    try {
      const adSelector = isDev ? `div.adsbygoogle.${adClass}` : `ins.adsbygoogle.${adClass}`
      const targetAdSlot = document.querySelector(adSelector)

      if (!targetAdSlot) {
        console.log(`*SKIP* [targetAdSlot] not found for [adClass]: ${adClass}`)
        return
      }

      if (isDev) {
        fromContent
          ? key.startsWith(currentRoute) && pushedAds.current.add(key)
          : pushedAds.current.add(key)
        setPreviousRoute(currentRoute)
        return
      }

      if (!targetAdSlot.getAttribute('data-adsbygoogle-status')) {
        pushedAds.current.add(key)
        setPreviousRoute(currentRoute)
      }
    } catch (error) {
      console.debug('*** Google Adsense component error', error)
    }
  }, [adKey, adPosition, isDev, currentRoute, fromContent, key, adClass, pushedAds, previousRoute, setPreviousRoute, adsRequestSent, setAdsRequestSent])

  if (isDev) {
    const statusOptions = ['filled', 'unfilled', undefined]
    const status = statusOptions[Math.floor(Math.random() * statusOptions.length)]

    return (
      <>
        <div key={key} className='google_adsense_container_placeholder'>
          <div
            className={`adsbygoogle ${adClass}`}
            data-ad-slot={selectedAdUnit}
            data-ad-variant={selectedVariant.key}
            {...(status ? { 'data-ad-status': status } : {})}
          >
            PLACEHOLDER GOOGLE ADSENSE ADVERT<br />
            variant: {selectedVariant.key}<br />
            adUnit: {selectedAdUnit}<br />
            adSource: {adSource}<br />
            adKey: {adKey}<br />
            adPosition: {adPosition}<br />
            uniqueKey: {key}
          </div>
        </div>
        <GoogleAdCta isSidePanelAd={isSidePanelAd} />
      </>
    )
  }

  return (
    <div key={key}>
      <ReactAdSense adUnit={selectedAdUnit} adClass={adClass} />
      <GoogleAdCta isSidePanelAd={isSidePanelAd} />
    </div>
  )
}

export default GoogleAd
